'use client';

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from 'react';
import { type ImageType } from '@/common/types/image.type';
import { isDesktop } from 'react-device-detect';
import { useGetStoreProductItem } from '@/common/apis/storefronts/v1/get-product.api';
import { useGetVendorItem } from '@/common/apis/storefronts/v1/get-vendor.api';
import Footer from '@/common/components/footer';
import Layout from '@/common/components/layout';
import { ResponsiveWrapper } from '@/common/components/wrapper';
import { appConfigs } from '@/common/configs/app.config';
import { ProductContext } from '@/common/contexts/product.context';
import { useBasket } from '@/common/hooks/use-basket.hook';
import { useProductCart } from '@/common/hooks/use-product-cart.hook';
import { useProductPageNavigation } from '@/common/hooks/use-product-page-navigation.hook';
import { useProductVariant } from '@/common/hooks/use-product-variant.hook';
import { rialToToman } from '@/common/utils/money-exchange.utility';
import Body from './body.component';
import Header from './header.component';
import ProductGallery from './image-gallery.component';
import ProductErrorState from './product-error-state.component';
import ProductLoadingSpinner from './product-loading-spinner.component';

export default function Main(): ReactElement {
  const { pageState, productIdentifier, productId, navigateToVendor } =
    useProductPageNavigation();

  const { productVariant, setVariant } = useProductVariant();
  const [selectedImage, setSelectedImage] = useState(0);
  const { setVendor } = useBasket(0);
  const firstLoad = useRef(true);
  const basketSuccessHandled = useRef(false);

  const { data: product, error } = useGetStoreProductItem({
    query: {
      identifier: productIdentifier,
      id: productId,
    },
    disableToast: true,
  });

  const { data: vendorInfo } = useGetVendorItem({
    query: { identifier: productIdentifier },
    disableToast: true,
  });

  const { addToCart, handleBasketSuccess, isPending, basketSuccess } =
    useProductCart({
      productId,
      productVariant,
      productVariants: product?.productVariants,
      vendorSlug: productIdentifier,
    });

  const AllImages: ImageType[] = [
    ...(product?.productMedia.thumbnailImageProduct
      ? [product.productMedia.thumbnailImageProduct]
      : []),
    ...(Array.isArray(product?.productMedia?.productImagesProduct)
      ? product.productMedia.productImagesProduct
      : []),
  ];

  const formmatedPrice = rialToToman(
    product?.price || 0,
    appConfigs.rialExchangeRate
  );

  const productDetails = {
    id: product?.id.toString(),
    name: product?.name,
    categoryName: product?.categoryName,
    price: formmatedPrice,
    shippingFeeAmount: product?.shippingFeeAmount,
    discount: Math.round((product?.discount ?? 0) * 100),
    availability: product?.availability,
  };

  useEffect(() => {
    if (vendorInfo?.data?.id) setVendor(vendorInfo?.data.id);
  }, [vendorInfo, setVendor]);

  useEffect(() => {
    if (firstLoad.current) {
      firstLoad.current = false;
    }
  }, []);

  useEffect(() => {
    if (basketSuccess && vendorInfo?.data && !basketSuccessHandled.current) {
      basketSuccessHandled.current = true;
      handleBasketSuccess(vendorInfo?.data);
    }
  }, [basketSuccess, handleBasketSuccess, vendorInfo?.data]);

  // Reset the basketSuccessHandled ref when basketSuccess becomes false or when starting a new request
  useEffect(() => {
    if (!basketSuccess || isPending) {
      basketSuccessHandled.current = false;
    }
  }, [basketSuccess, isPending]);

  const contextValue = useMemo(
    () => ({
      selectedVariants: productVariant,
      selectedImageIndex: selectedImage,
      setSelectedImageIndex: setSelectedImage,
    }),
    [productVariant, selectedImage, setSelectedImage]
  );

  if (error?.message.includes('400')) {
    return <ProductErrorState onBackToStore={navigateToVendor} />;
  }

  return (
    <ProductContext.Provider value={contextValue}>
      {pageState ? (
        <Layout
          responsiveAppbar={true}
          appbar={{
            showSearch: isDesktop,
            title: 'تصاویر محصول',
            hamburger: false,
            cartWidget: false,
          }}
        >
          <ResponsiveWrapper className="px-4 md:px-0 lg:pt-6">
            <ProductGallery AllImages={AllImages} />
          </ResponsiveWrapper>
        </Layout>
      ) : (
        <>
          <Layout
            withFixedButton={!isDesktop}
            responsiveAppbar={true}
            className="bg-silver-100"
            appbar={{
              showSearch: isDesktop,
              storeWidget: true,
              cartWidget: true,
            }}
          >
            <>
              {product ? (
                <>
                  <Header
                    vendor={vendorInfo?.data}
                    productMedia={product.productMedia}
                    productDetails={productDetails}
                    productVariants={product.productVariants}
                    variants={product.variants}
                    price={formmatedPrice}
                    discount={Math.round(product.discount * 100)}
                    setVariant={setVariant}
                    description={product.description}
                    isPending={isPending}
                    addToCart={addToCart}
                  />

                  <Body
                    productDescription={product.description}
                    productId={productId}
                    vendorId={product.vendorId}
                  />
                </>
              ) : (
                <ProductLoadingSpinner />
              )}
            </>
          </Layout>
          <Footer
            className="hidden w-full lg:block"
            offsetMobileNav={false}
          />
        </>
      )}
    </ProductContext.Provider>
  );
}
